import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import {
  buildOpenChatSessionsQuery,
  chatStatusLabel,
} from '../../lib/chatQueries';

/**
 * Chat Monitor — real-time view of active chat sessions and agent activity
 * within the CRM section. Read‑only dashboard; agent assignment lives in
 * Chat Operations.
 */
export default function ChatMonitor({ userData }) {
  const { companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId || null;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!resolvedCompanyId) {
      setLoading(false);
      setSessions([]);
      return undefined;
    }

    setLoading(true);
    setError('');

    let unsub;
    try {
      const q = buildOpenChatSessionsQuery(db, resolvedCompanyId);
      unsub = onSnapshot(
        q,
        (snap) => {
          const rows = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setSessions(rows);
          setLoading(false);
          setError('');
        },
        (err) => {
          console.error('ChatMonitor snapshot error:', err);
          setError(err.message || 'Failed to load sessions.');
          setLoading(false);
        },
      );
    } catch (err) {
      console.error('ChatMonitor query error:', err);
      setError(err.message || 'Failed to build query.');
      setLoading(false);
    }

    return () => {
      if (unsub) unsub();
    };
  }, [resolvedCompanyId]);

  /** Aggregate agent engagement counts from active sessions */
  const agentStats = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      const agent =
        (typeof s.assignedAgent === 'string' && s.assignedAgent.trim()) ||
        (typeof s.agentName === 'string' && s.agentName.trim()) ||
        'Unassigned';
      if (!map[agent]) {
        map[agent] = { agent, count: 0, sessions: [] };
      }
      map[agent].count += 1;
      map[agent].sessions.push(s);
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [sessions]);

  if (!resolvedCompanyId) {
    return (
      <Alert severity="warning">
        No company context available. Please select a workspace to monitor chats.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Summary cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Active Sessions
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {sessions.length}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Agents Engaged
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {agentStats.filter((a) => a.agent !== 'Unassigned').length}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Unassigned
            </Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">
              {(agentStats.find((a) => a.agent === 'Unassigned') || { count: 0 }).count}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Open / Active
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {
                sessions.filter((s) => s.status === 'open' || s.status === 'active').length
              }
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Agent workload summary */}
      {agentStats.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Agent Workload
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {agentStats.map((a) => (
                <Chip
                  key={a.agent}
                  label={`${a.agent}: ${a.count}`}
                  size="small"
                  color={a.agent === 'Unassigned' ? 'warning' : 'primary'}
                  variant={a.agent === 'Unassigned' ? 'outlined' : 'filled'}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sessions table */}
      {sessions.length === 0 ? (
        <Alert severity="info">No active chat sessions at the moment.</Alert>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Visitor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Last Activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {s.visitorName || s.visitorId || s.email || 'Anonymous'}
                    </Typography>
                    {s.email && (
                      <Typography variant="caption" color="text.secondary">
                        {s.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={chatStatusLabel(s.status)}
                      size="small"
                      color={
                        s.status === 'open' || s.status === 'active'
                          ? 'success'
                          : s.status === 'pending'
                            ? 'warning'
                            : 'info'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {s.assignedAgent || s.agentName || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {s.updatedAt?.toDate
                        ? new Date(s.updatedAt.toDate()).toLocaleString()
                        : s.updatedAt
                          ? new Date(s.updatedAt).toLocaleString()
                          : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
